import React, { useEffect, useState } from "react";
import {
  Card, CardBody, Button, Row, Col, Modal, ModalHeader,
  ModalBody, ModalFooter, Form, FormGroup, Label, Input,
  Container, Badge, Spinner
} from "reactstrap";
import {
  FaUsers, FaPlus, FaEdit, FaUserShield,
  FaPhone, FaEnvelope, FaLock, FaToggleOn, FaToggleOff
} from "react-icons/fa";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { userActions } from "../../sagas/userSlice";

const UserPage = () => {
  const dispatch = useDispatch();
  const { users, loading } = useSelector((state) => state.users);

  const [addModal, setAddModal] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  useEffect(() => {
    dispatch(userActions.list());
  }, [dispatch]);

  const toggleAddModal = () => setAddModal(!addModal);
  const toggleResetModal = () => setResetModal(!resetModal);

  /* ============================
     ACTIVATE / DEACTIVATE USER
  ============================ */
  const handleToggleActive = (user) => {
    Swal.fire({
      title: user.actif ? "Désactiver cet utilisateur ?" : "Activer cet utilisateur ?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Oui",
    }).then((result) => {
      if (result.isConfirmed) {
        if (user.actif) {
          dispatch(userActions.deactivateUser({
            id: user.id,
            onSuccess: () => Swal.fire("Succès", "Utilisateur désactivé", "success"),
          }));
        } else {
          dispatch(userActions.activateUser({
            id: user.id,
            onSuccess: () => Swal.fire("Succès", "Utilisateur activé", "success"),
          }));
        }
      }
    });
  };

  /* ============================
     RESET PASSWORD
  ============================ */
  const openReset = (user) => {
    setResetUser(user);
    setNewPassword("");
    setResetModal(true);
  };

  const submitResetPassword = () => {
    dispatch(userActions.resetPassword({
      id: resetUser.id,
      newPassword,
      onSuccess: () => {
        Swal.fire("Succès", "Mot de passe réinitialisé", "success");
        toggleResetModal();
      }
    }));
  };

  /* ============================
     ADD / EDIT FORMIK
  ============================ */
  const validationSchema = Yup.object({
    firstName: Yup.string().required("Prénom obligatoire"),
    lastName: Yup.string().required("Nom obligatoire"),

    username: Yup.string()
      .matches(/^[0-9]+$/, "Nom d'utilisateur doit contenir uniquement des chiffres")
      .required("Nom d'utilisateur obligatoire"),

    role: Yup.string().required("Rôle obligatoire"),
  });

  const addFormik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      username: "",
      password: "",
      role: "ROLE_CAISSE",
      businessName: "",
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      dispatch(userActions.addUser({
        data: values,
        onSuccess: () => {
          Swal.fire("Succès", "Utilisateur ajouté avec succès", "success");
          resetForm();
          toggleAddModal();
          dispatch(userActions.list());
        }
      }));
    }
  });

  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: editUser || {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      username: "",
      role: "ROLE_CAISSE"
    },
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      console.log(values)
      dispatch(userActions.updateUser({
        id: editUser.id,
        data: values,
        onSuccess: () => {
          Swal.fire("Succès", "Utilisateur modifié avec succès", "success");
          resetForm();
          setEditUser(null);   // ✅ CLOSE MODAL
          dispatch(userActions.list());
        }
      }));
    }
  });

  return (
    <Container fluid>
      {/* HEADER */}
      <Row className="mb-3">
        <Col>
          <Card className="shadow-sm border-0">
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 text-primary">
                <FaUsers className="me-2" /> Gestion des utilisateurs
              </h4>
              <Button color="primary" onClick={toggleAddModal}>
                <FaPlus className="me-1" /> Nouvel utilisateur
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* LISTE */}
      {loading ? (
        <div className="text-center py-5"><Spinner /></div>
      ) : (
        <Row>
          {users.map(user => (
            <Col md={4} key={user.id} className="mb-3">
              <Card className="shadow-sm border-0 h-100 user-card">
                <CardBody>
                  <div className="d-flex justify-content-between">
                    <h5>
                      {user.firstName} {user.lastName}
                      <Badge color="info" className="ms-2">
                        <FaUserShield /> {user.role}
                      </Badge>
                      <Badge color={user.actif ? "success" : "danger"} className="ms-2">
                        {user.actif ? "Actif" : "Inactif"}
                      </Badge>
                    </h5>
                    <Button size="sm" color="warning" onClick={() => {
                      setEditUser(user);
                      editFormik.resetForm({ values: user });
                    }}>
                      <FaEdit />
                    </Button>
                  </div>

                  <p className="text-muted mb-1">
                    <FaEnvelope className="me-2" /> {user.email}
                  </p>
                  <p className="text-muted mb-2">
                    <FaPhone className="me-2" /> {user.phoneNumber || "-"}
                  </p>

                  {/* ACTIONS */}
                  <div className="d-flex gap-2 mt-3 flex-wrap">
                    <Button
                      size="sm"
                      color={user.actif ? "secondary" : "success"}
                      onClick={() => handleToggleActive(user)}
                    >
                      {user.actif ? <FaToggleOff /> : <FaToggleOn />}{" "}
                      {user.actif ? "Désactiver" : "Activer"}
                    </Button>
                    <Button size="sm" color="dark" onClick={() => openReset(user)}>
                      <FaLock /> Changer mot de passe
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
          {users.length === 0 && (
            <Col className="text-center py-5">
              <h5>Aucun utilisateur trouvé</h5>
            </Col>
          )}
        </Row>
      )}

      {/* ADD MODAL */}
      <Modal isOpen={addModal} toggle={toggleAddModal}>
        <Form onSubmit={addFormik.handleSubmit}>
          <ModalHeader toggle={toggleAddModal}>Ajouter un utilisateur</ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Prénom</Label>
                  <Input {...addFormik.getFieldProps("firstName")} />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Nom</Label>
                  <Input {...addFormik.getFieldProps("lastName")} />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Email</Label>
              <Input {...addFormik.getFieldProps("email")} />
            </FormGroup>
            <FormGroup>
              <Label>Téléphone</Label>
              <Input {...addFormik.getFieldProps("phoneNumber")} />
            </FormGroup>
            <FormGroup>
              <Label>Nom d'utilisateur</Label>
              <Input
                name="username"
                value={addFormik.values.username}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  addFormik.setFieldValue("username", onlyDigits);
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label>Mot de passe</Label>
              <Input
                type="password"
                name="password"
                value={addFormik.values.password}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, "");
                  addFormik.setFieldValue("password", onlyDigits);
                }}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </FormGroup>
            <FormGroup>
              <Label>Rôle</Label>
              <Input type="select" {...addFormik.getFieldProps("role")}>
                <option value="ROLE_ADMIN">Admin</option>
                <option value="ROLE_SUPER_ADMIN">Super Admin</option>
                  <option value="ROLE_CAISSE">Caissier</option>
              </Input>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" type="submit">Ajouter</Button>
            <Button onClick={toggleAddModal}>Annuler</Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* EDIT MODAL */}
      <Modal isOpen={!!editUser} toggle={() => setEditUser(null)}>
        {editUser && (
          <Form onSubmit={editFormik.handleSubmit}>
            <ModalHeader toggle={() => setEditUser(null)}>Modifier l'utilisateur</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Prénom</Label>
                <Input
                  {...editFormik.getFieldProps("firstName")}
                />
              </FormGroup>
              <FormGroup>
                <Label>Nom</Label>
                <Input {...editFormik.getFieldProps("lastName")} />
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input {...editFormik.getFieldProps("email")} />
              </FormGroup>
              <FormGroup>
                <Label>Téléphone</Label>
                <Input {...editFormik.getFieldProps("phoneNumber")} />
              </FormGroup>
              <FormGroup>
                <Label>Nom d'utilisateur</Label>
                <Input
                  name="username"
                  value={editFormik.values.username}
                  onChange={(e) => {
                    const onlyDigits = e.target.value.replace(/\D/g, "");
                    editFormik.setFieldValue("username", onlyDigits);
                  }}
                />
              </FormGroup>
              <FormGroup>
                <Label>Rôle</Label>
                <Input type="select" {...editFormik.getFieldProps("role")}>
                  <option value="ROLE_ADMIN">Admin</option>
                  <option value="ROLE_SUPER_ADMIN">Super Admin</option>
                  <option value="ROLE_CAISSE">Caissier</option>
                </Input>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="primary" type="submit">Enregistrer</Button>
              <Button onClick={() => setEditUser(null)}>Annuler</Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>

      {/* RESET PASSWORD MODAL */}
      <Modal isOpen={resetModal} toggle={toggleResetModal}>
        <ModalHeader toggle={toggleResetModal}>Réinitialiser mot de passe</ModalHeader>
        <ModalBody>
          <p>Utilisateur : <b>{resetUser?.username}</b></p>
          <FormGroup>
            <Label>Nouveau mot de passe</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => {
                const onlyDigits = e.target.value.replace(/\D/g, "");
                setNewPassword(onlyDigits);
              }}
              inputMode="numeric"
              pattern="[0-9]*"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={submitResetPassword}>Confirmer</Button>
          <Button onClick={toggleResetModal}>Annuler</Button>
        </ModalFooter>
      </Modal>
    </Container>
  );
};

export default UserPage;
